// The roll moment, over the board. The die tumbles across the board frame and
// lands beside center stage, the card is drawn, holds, then flies to the hand. When the match ends,
// the stage holds the final card until a new match starts. The stage steps
// aside while the board shows an earlier step.

import { ENERGY_DIE_LABELS, WHEEL_DIE_LABELS } from '../content/casters';
import { END_CARD } from '../content/copy';
import { OWNER_NAMES } from '../engine/describe';
import { el } from '../lib/dom';
import { otherSeat } from '../room/protocol';
import { spellById, spellClasses } from '../spells/library';
import { deliveryStore } from '../state/energyDelivery';
import { gameStore } from '../state/game';
import { historyStore, isReviewing } from '../state/history';
import { localSide } from '../state/localSide';
import { startNewMatch } from '../state/newMatch';
import { voteRoomRematch } from '../state/room';
import { isInRoom, roomStore } from '../state/roomStore';
import { putAwayFinalCard, type StageState, stageStore } from '../state/stage';
import { createSpellCardFace } from './SpellCardFace';
import { createTumblingDie } from './TumblingDie';

const SEE_BOARD_LABEL = 'See the board';
/** Plain words, not final copy. */
const NEW_MATCH_LABEL = 'New match';
const REMATCH_LABEL = 'Rematch';
const REMATCH_ASKED_LABEL = 'Rematch? Your friend is in';
const REMATCH_WAIT_LABEL = 'Waiting for your friend';

/** `rollButton` is where the die comes from. */
export const createStage = (rollButton: HTMLElement): HTMLElement => {
  // The slot holds the place where the die lands. The die itself is drawn over the whole stage.
  const dieSlot = el('div', { className: 'stage__die' });
  const die = createTumblingDie(dieSlot, rollButton);
  /** The roll the die tumbled for, so a redraw does not roll it again. */
  let tumbledSerial = 0;

  const card = createSpellCardFace('card--stage');

  // The final card.
  const endTitle = el('h2', { className: 'stage__end-title' });
  const endLine = el('p', { className: 'stage__end-line' });
  const endSub = el('p', { className: 'stage__end-sub' });
  // In a room the button is a vote for a rematch. The room starts it when both people vote.
  const again = el('button', {
    className: 'button button--primary',
    type: 'button',
    textContent: NEW_MATCH_LABEL,
    onclick: () => (isInRoom() ? voteRoomRematch() : startNewMatch()),
  });
  const drawAgain = (): void => {
    const room = roomStore.get();
    const seat = room?.seat ?? null;
    again.hidden = room !== null && seat === null;
    if (!room || !seat) {
      again.textContent = NEW_MATCH_LABEL;
      again.disabled = false;
      return;
    }
    const friendVoted = room.view?.rematch[otherSeat(seat)] ?? false;
    again.textContent = room.votedRematch
      ? REMATCH_WAIT_LABEL
      : friendVoted
        ? REMATCH_ASKED_LABEL
        : REMATCH_LABEL;
    again.disabled = room.votedRematch;
  };
  drawAgain();
  roomStore.subscribe(drawAgain);
  // Plain words, not final copy. The button puts the card away so the final board shows.
  const seeBoard = el('button', {
    className: 'button button--pass-through',
    type: 'button',
    textContent: SEE_BOARD_LABEL,
    onclick: putAwayFinalCard,
  });
  const endActions = el('div', { className: 'stage__end-actions' }, [again, seeBoard]);
  const endCard = el('div', { className: 'stage__end' }, [endTitle, endLine, endSub, endActions]);

  const root = el('div', { className: 'stage', hidden: true }, [
    die.svg,
    dieSlot,
    card.root,
    endCard,
  ]);

  const drawEnd = (): void => {
    const state = gameStore.get();
    const winner = state.winner;
    const mine = winner === localSide();
    root.className = `stage stage--over stage--${winner ?? 'none'}`;
    endTitle.textContent = winner ? `${OWNER_NAMES[winner]} wins` : 'Nobody wins';
    endLine.textContent = state.endLine;
    endSub.textContent =
      winner === null ? END_CARD.draw : mine ? END_CARD.playerWon : END_CARD.playerLost;
  };

  const draw = (stage: StageState): void => {
    const showing = stage.phase !== 'hidden' && !isReviewing();
    root.hidden = !showing;
    if (!showing) {
      die.stop();
      tumbledSerial = 0;
      return;
    }

    const over = stage.phase === 'over';
    dieSlot.hidden = over;
    card.root.hidden = over;
    endCard.hidden = !over;
    if (over) {
      die.stop();
      drawEnd();
      return;
    }
    if (!stage.roll) return;

    // A roll for energy has no card. The die is all the stage shows. A roll that gave a scroll shows the scroll.
    const { spellId, caster } = stage.roll;
    const spell = spellId === null ? null : spellById(spellId);
    card.root.hidden = spell === null;
    // The die has the color of the side that rolled.
    root.className = `stage stage--${stage.phase} stage--by-${caster}${spell ? ` ${spellClasses('stage', spell)}` : ' stage--energy'}`;
    if (spellId !== null) card.draw({ spellId, caster }, '');
    // The die reads the layout, so it goes after the card and the classes.
    if (stage.phase !== 'spinning') die.rest(stage.roll.face);
    else if (stage.roll.serial !== tumbledSerial) {
      tumbledSerial = stage.roll.serial;
      die.tumble(stage.roll.face);
    }
  };

  // The die reads energy in a match with casters, and the numbers of the wheel in the other mode.
  const drawLabels = (): void =>
    die.label(gameStore.get().mode === 'gods' ? ENERGY_DIE_LABELS : WHEEL_DIE_LABELS);
  drawLabels();
  gameStore.subscribe(drawLabels);

  // The die squashes each time a coin of energy leaves it.
  let spat = 0;
  deliveryStore.subscribe((delivery) => {
    if (delivery.left > spat) die.spit(delivery.left - 1);
    spat = delivery.left;
  });

  draw(stageStore.get());
  stageStore.subscribe(draw);
  historyStore.subscribe(() => draw(stageStore.get()));
  gameStore.subscribe(() => {
    if (stageStore.get().phase === 'over') drawEnd();
  });
  return root;
};
